import { z } from "zod";
import { jsonValueSchema } from "@common/schemas/json.schema";
import { firmSkillRevisionIdsSchema } from "@common/schemas/domains/agentSkills/agent-skill.schema";
import {
    AGENT_RUN_STEPS,
    AgentRunJsonValue,
    AgentRunMode,
    AgentRunProposalStatus,
    AgentRunState,
    AgentRunWaitKind,
} from "@common/types/agentRun";

export const agentRunJsonValueSchema: z.ZodType<AgentRunJsonValue> =
    jsonValueSchema;

export const agentRunJsonObjectSchema = z.record(agentRunJsonValueSchema);

export const waitingOnEntrySchema = z.object({
    taskId: z.string().uuid(),
    threadId: z.string().uuid(),
    kind: z.nativeEnum(AgentRunWaitKind),
    sinceMessageId: z.string().uuid().nullable(),
    sinceCreatedAt: z.string().nullable().optional(),
    proposalRefs: z.array(z.string()).optional(),
    question: z.string().optional(),
});
export type WaitingOnEntry = z.infer<typeof waitingOnEntrySchema>;

export const agentRunProposalItemSchema = z.object({
    id: z.string(),
    rowKeys: z.array(z.union([z.string(), z.number()])),
});
export type AgentRunProposalItem = z.infer<typeof agentRunProposalItemSchema>;

export const agentRunProposalToolSchema = z.enum([
    "journals_act",
    "transactions_act",
    "transactions_transfer_matches_act",
]);
export type AgentRunProposalTool = z.infer<typeof agentRunProposalToolSchema>;

export const agentRunProposalClaimSchema = z.object({
    attemptId: z.string(),
    claimedAt: z.string(),
    itemIds: z.array(z.string()).nullable(),
    previousStatus: z.nativeEnum(AgentRunProposalStatus),
});
export type AgentRunProposalClaim = z.infer<typeof agentRunProposalClaimSchema>;

export const agentRunProposalSchema = z.object({
    id: z.string(),
    ref: z.string(),
    taskId: z.string().uuid(),
    messageId: z.string().uuid(),
    tool: agentRunProposalToolSchema,
    arguments: agentRunJsonObjectSchema,
    items: z.array(agentRunProposalItemSchema).optional(),
    status: z.nativeEnum(AgentRunProposalStatus),
    summary: z.string().optional(),
    claim: agentRunProposalClaimSchema.optional(),
});
export type AgentRunProposal = z.infer<typeof agentRunProposalSchema>;

export const agentRunScopeSchema = z.object({
    closeId: z.string().uuid(),
});
export type AgentRunScope = z.infer<typeof agentRunScopeSchema>;

// What the run reads back at every segment: the defaults line, the latest
// state line, the exceptions register as the skills' table, and the latest
// block of each stage, keyed by stage key. The run task comment shows the
// same text to people and is never parsed.
export const agentRunTaskMemorySchema = z.object({
    defaultsLine: z.string().nullable(),
    stateLine: z.string().nullable(),
    register: z.string().nullable(),
    stageBlocks: z.record(z.string().nullable()),
});
export type AgentRunTaskMemory = z.infer<typeof agentRunTaskMemorySchema>;

// The last progress marker the worker reported: the step vocabulary of the
// Datadog `agent.step` events plus one short human line for the Agent panel.
export const agentRunProgressSchema = z.object({
    step: z.enum(AGENT_RUN_STEPS),
    detail: z.string().nullable(),
    at: z.string(),
    segment: z.number().int(),
});
export type AgentRunProgress = z.infer<typeof agentRunProgressSchema>;

// The stored row keeps whatever step a worker wrote, so a step added or
// removed in a later deploy never fails the list response; the summary
// narrows it back to the API enum.
export const agentRunStoredProgressSchema = agentRunProgressSchema.extend({
    step: z.string(),
});
export type AgentRunStoredProgress = z.infer<
    typeof agentRunStoredProgressSchema
>;

export const agentRunRowSchema = z.object({
    id: z.string().uuid(),
    runScope: agentRunScopeSchema.nullable(),
    workspaceId: z.string().uuid(),
    entityUuid: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
    runTaskId: z.string().uuid(),
    startedByUserId: z.number().int(),
    workflowId: z.string(),
    state: z.nativeEnum(AgentRunState),
    stageKey: z.string().nullable(),
    waitingOn: z.array(waitingOnEntrySchema),
    proposals: z.array(agentRunProposalSchema),
    memory: agentRunTaskMemorySchema.nullable(),
    stepCount: z.number().int(),
    toolInvocationCount: z.number().int(),
    segmentCount: z.number().int(),
    expiresAt: z.coerce.date().nullable(),
    pausedAt: z.coerce.date().nullable(),
    finishReason: z.string().nullable(),
    progress: agentRunStoredProgressSchema.nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type AgentRunRow = z.infer<typeof agentRunRowSchema>;

export const agentRunSummaryWaitSchema = z.object({
    taskId: z.string().uuid(),
    threadId: z.string().uuid(),
    kind: z.nativeEnum(AgentRunWaitKind),
    question: z.string().nullable(),
    title: z.string().nullable(),
});

// What a row renders without reading the run's workspace: the entity avatar
// and the starter's avatar come with the summary.
export const agentRunSummaryEntitySchema = z.object({
    name: z.string().nullable(),
    iconColor: z.string(),
    logo: z.string().nullable(),
    isPersonal: z.boolean(),
});
export type AgentRunSummaryEntity = z.infer<typeof agentRunSummaryEntitySchema>;

export const agentRunSummaryStarterSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    avatar: z.string().nullable(),
    iconColor: z.string(),
    email: z.string().nullable(),
});
export type AgentRunSummaryStarter = z.infer<
    typeof agentRunSummaryStarterSchema
>;

export const agentRunSummarySchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    workspaceName: z.string().nullable(),
    runTaskId: z.string().uuid(),
    entityUuid: z.string().uuid(),
    entity: agentRunSummaryEntitySchema.nullable(),
    close: z
        .object({
            id: z.string().uuid(),
            name: z.string(),
            startDate: z.string().nullable(),
            endDate: z.string().nullable(),
        })
        .nullable(),
    state: z.nativeEnum(AgentRunState),
    pausedAt: z.string().nullable(),
    finishReason: z.string().nullable(),
    stage: z.object({
        key: z.string().nullable(),
        label: z.string().nullable(),
    }),
    stateLine: z.string().nullable(),
    startedBy: agentRunSummaryStarterSchema,
    closeTasks: z
        .object({ done: z.number().int(), total: z.number().int() })
        .nullable(),
    progress: agentRunProgressSchema.nullable(),
    waitingOn: z.array(agentRunSummaryWaitSchema),
    counters: z.object({
        segmentCount: z.number().int(),
        stepCount: z.number().int(),
        toolInvocationCount: z.number().int(),
    }),
    startedByUserId: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export type AgentRunSummary = z.infer<typeof agentRunSummarySchema>;

export const agentRunListResponseSchema = z.object({
    runs: z.array(agentRunSummarySchema),
});

export const agentRunPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    runId: z.string().uuid(),
});
export type AgentRunListResponse = z.infer<typeof agentRunListResponseSchema>;

export const agentRunControlResponseSchema = z.object({
    run: agentRunSummarySchema,
});
export type AgentRunControlResponse = z.infer<
    typeof agentRunControlResponseSchema
>;

export const agentRunUsageRowSchema = z.object({
    id: z.string().uuid(),
    runId: z.string().uuid(),
    workspaceId: z.string().uuid(),
    organizationId: z.string().uuid().nullable(),
    userId: z.number().int(),
    agentKind: z.string(),
    sessionId: z.string(),
    taskLabel: z.string(),
    segment: z.number().int(),
    attemptId: z.string(),
    stage: z.string(),
    mode: z.nativeEnum(AgentRunMode),
    outcome: z.string(),
    model: z.string().nullable(),
    llmCallCount: z.number().int(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    cachedTokens: z.number().int(),
    reasoningTokens: z.number().int(),
    toolCallCount: z.number().int(),
    durationMs: z.number().int(),
    costUsd: z.number().nullable(),
    firmSkillRevisionIds: firmSkillRevisionIdsSchema.nullable(),
    createdAt: z.coerce.date(),
});
export type AgentRunUsageRow = z.infer<typeof agentRunUsageRowSchema>;

export const agentRunStartBodySchema = z.object({
    entityId: z.number().int().positive(),
    closeId: z.string().uuid(),
});
export type AgentRunStartBody = z.infer<typeof agentRunStartBodySchema>;

export const agentRunStartResponseSchema = z.object({
    runId: z.string().uuid(),
    closeId: z.string().uuid(),
    runTaskId: z.string().uuid(),
});
export type AgentRunStartResponse = z.infer<typeof agentRunStartResponseSchema>;
